import { z } from 'zod';

/**
 * Vector ink storage (spec §4.3). Coordinates are in *logical page units* — a fixed document space
 * independent of zoom, device pixel ratio or orientation. A page is PAGE_WIDTH × PAGE_HEIGHT units
 * (A4 aspect ratio). Rendering maps document space → screen with a single affine transform.
 *
 * A point is stored as a compact tuple: [x, y, t, pressure, tiltX, tiltY]
 *   x, y      document units (float, 2 decimals)
 *   t         ms since stroke start (int)
 *   pressure  0..1 (0.5 when the device reports none)
 *   tiltX/Y   degrees −90..90 (0 when unavailable)
 */
export const PAGE_WIDTH = 1000;
export const PAGE_HEIGHT = 1414;
/** Pages grow downwards while the learner writes (an endless notebook sheet), up to 4 × A4. */
export const PAGE_MAX_HEIGHT = PAGE_HEIGHT * 4;
export const PAGE_GROW_STEP = PAGE_HEIGHT / 2;
/** Writing closer than this to the bottom edge extends the page. */
export const PAGE_GROW_MARGIN = 300;
export const INK_SCHEMA_VERSION = 1;

export const MAX_POINTS_PER_STROKE = 20_000;
export const MAX_STROKES_PER_PAGE = 20_000;
export const MAX_PAGES_PER_SESSION = 30;

export const PointSchema = z.tuple([
  z.number().min(-PAGE_WIDTH).max(PAGE_WIDTH * 2),
  z.number().min(-PAGE_HEIGHT).max(PAGE_MAX_HEIGHT + PAGE_HEIGHT),
  z.number().int().min(0).max(3_600_000),
  z.number().min(0).max(1),
  z.number().min(-90).max(90),
  z.number().min(-90).max(90),
]);
export type InkPoint = z.infer<typeof PointSchema>;

export const INK_TOOLS = ['pen', 'pencil', 'marker'] as const;

export const StrokeSchema = z.object({
  id: z.uuid(),
  tool: z.enum(INK_TOOLS),
  /** CSS hex colour. */
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  /** Nominal width in document units. */
  width: z.number().min(0.5).max(40),
  /** Input device the stroke was drawn with. */
  device: z.enum(['pen', 'touch', 'mouse']),
  createdAt: z.number().int().nonnegative(),
  points: z.array(PointSchema).min(1).max(MAX_POINTS_PER_STROKE),
});
export type Stroke = z.infer<typeof StrokeSchema>;

export const PAGE_TEMPLATES = ['lined', 'grid', 'blank', 'exam'] as const;
export const PageTemplateSchema = z.enum(PAGE_TEMPLATES);
export type PageTemplate = z.infer<typeof PageTemplateSchema>;

export const PageDocumentSchema = z.object({
  schemaVersion: z.literal(INK_SCHEMA_VERSION),
  width: z.literal(PAGE_WIDTH),
  height: z.number().int().min(PAGE_HEIGHT).max(PAGE_MAX_HEIGHT),
  template: PageTemplateSchema,
  strokes: z.array(StrokeSchema).max(MAX_STROKES_PER_PAGE),
});
export type PageDocument = z.infer<typeof PageDocumentSchema>;

export const SaveStrokesRequestSchema = z.object({
  /** Client-generated id of this revision; repeating it makes the request idempotent. */
  clientRevisionId: z.uuid(),
  /** The server revision this edit is based on (0 for a brand-new page). */
  baseRevision: z.number().int().min(0),
  /** Position of the page in the session; used when the page is created by this save. */
  pageOrder: z.number().int().min(0).max(MAX_PAGES_PER_SESSION - 1),
  /** Explicit conflict resolution: overwrite even if the server moved on. Only sent after user choice. */
  force: z.boolean().optional(),
  document: PageDocumentSchema,
});
export type SaveStrokesRequest = z.infer<typeof SaveStrokesRequestSchema>;

export const SaveStrokesResponseSchema = z.object({
  pageId: z.uuid(),
  revision: z.number().int(),
  sha256: z.string(),
  updatedAt: z.string(),
  strokeCount: z.number().int(),
});
export type SaveStrokesResponse = z.infer<typeof SaveStrokesResponseSchema>;

export function emptyPage(template: PageTemplate = 'lined'): PageDocument {
  return { schemaVersion: INK_SCHEMA_VERSION, width: PAGE_WIDTH, height: PAGE_HEIGHT, template, strokes: [] };
}

/** Height the page should have so that a stroke reaching `maxY` keeps room below it. */
export function grownHeight(height: number, maxY: number): number {
  let h = height;
  while (maxY > h - PAGE_GROW_MARGIN && h < PAGE_MAX_HEIGHT) h = Math.min(PAGE_MAX_HEIGHT, Math.round(h + PAGE_GROW_STEP));
  return h;
}

/** Line spacing for the lined template, in document units. */
export const LINE_SPACING = 56;
export const PAGE_MARGIN_TOP = 120;
export const PAGE_MARGIN_LEFT = 90;
